(function (root) {
  function normalizeProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    return Object.fromEntries(Object.entries(properties)
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => [String(key), String(value)])
      .sort(([a], [b]) => a.localeCompare(b)));
  }

  function lineKey(item) {
    const properties = normalizeProperties(item.properties);
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || item.sellingPlanId || '';
    return JSON.stringify([String(item.variant_id || item.id || item.variantId || '').split('/').pop(), properties, String(sellingPlanId).split('/').pop()]);
  }

  function buildItems(items) {
    const grouped = new Map();
    for (const item of items || []) {
      const id = String(item.variantId || item.id || '').split('/').pop();
      const quantity = Number(item.quantity);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Invalid cart line.');
      const properties = normalizeProperties(item.properties);
      const sellingPlanId = String(item.sellingPlanId || item.selling_plan || '').split('/').pop();
      const key = JSON.stringify([id, properties, sellingPlanId]);
      const current = grouped.get(key) || { id: Number(id), quantity: 0, ...(Object.keys(properties).length ? { properties } : {}), ...(sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}) };
      current.quantity += quantity;
      grouped.set(key, current);
    }
    return [...grouped.values()];
  }

  function quantities(cart) {
    const result = new Map();
    for (const item of cart?.items || []) {
      const key = lineKey(item);
      result.set(key, (result.get(key) || 0) + Number(item.quantity || 0));
    }
    return result;
  }

  function classifyChange(before, after, requestedItems) {
    const wanted = new Map(buildItems(requestedItems).map((item) => [lineKey(item), item.quantity]));
    const oldLines = quantities(before);
    const newLines = quantities(after);
    let inserted = 0;
    let exact = true;
    let ambiguous = false;
    for (const [key, quantity] of wanted) {
      const delta = (newLines.get(key) || 0) - (oldLines.get(key) || 0);
      if (delta < 0 || delta > quantity) { exact = false; ambiguous = true; }
      inserted += Math.max(0, Math.min(quantity, delta));
      if (delta !== quantity) exact = false;
    }
    if (exact && wanted.size) return 'confirmed';
    if (ambiguous) return 'unknown';
    if (inserted > 0) return 'partial';
    if ([...wanted.keys()].every((key) => (newLines.get(key) || 0) === (oldLines.get(key) || 0))) return 'unchanged';
    return 'unknown';
  }

  async function addSelection({
    fetcher = root.fetch?.bind(root), cartUrl, cartAddUrl, snapshot,
    sectionIds = [], sectionsUrl = '/', requestId = '',
  }) {
    if (typeof fetcher !== 'function' || !cartUrl || !cartAddUrl || !snapshot || snapshot.state !== 'ready') {
      return { requestId, result: 'unchanged', stage: 'preflight', cart: null, postBody: null, error: 'Purchase selection is not ready.' };
    }
    const requestedItems = snapshot.items || [];
    if (!requestedItems.length) {
      return { requestId, result: 'unchanged', stage: 'preflight', cart: null, postBody: null, error: null };
    }
    let items;
    try { items = buildItems(requestedItems); }
    catch (error) {
      return { requestId, result: 'unchanged', stage: 'preflight', cart: null, postBody: null, error: error instanceof Error ? error.message : 'Invalid cart items.' };
    }
    const readCart = async () => {
      const response = await fetcher(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(`cart_read_${response.status}`);
      return response.json();
    };
    let before;
    try { before = await readCart(); }
    catch (error) {
      return { requestId, result: 'unchanged', stage: 'before_read', cart: null, postBody: null, error: error instanceof Error ? error.message : 'Cart could not be read.' };
    }

    let postBody = null;
    let postError = null;
    try {
      const response = await fetcher(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: [...new Set(sectionIds)].join(','),
          sections_url: sectionsUrl,
        }),
      });
      postBody = await response.json().catch(() => null);
      if (!response.ok || postBody?.status) postError = postBody?.description || postBody?.message || `cart_add_${response.status}`;
    } catch {
      postError = 'cart_add_response_unknown';
    }

    let cart = null;
    let result = 'unknown';
    try {
      cart = await readCart();
      result = classifyChange(before, cart, requestedItems);
    } catch {
      result = 'unknown';
    }
    const message = result === 'confirmed' ? '' : result === 'partial'
      ? 'Some items were added. Review your cart before trying again.'
      : result === 'unchanged' ? (postError || 'No items were added. Review your cart before trying again.')
        : 'The cart update could not be confirmed. Review your cart before trying again.';
    return {
      requestId,
      contextId: snapshot.contextId,
      selectionRevision: snapshot.selectionRevision,
      cart,
      result,
      stage: 'after_read',
      postBody,
      postError,
      ...(message ? { message } : {}),
    };
  }

  const api = Object.freeze({ buildItems, classifyChange, addSelection });
  root.SimpPurchaseCartV1 = root.SimpPurchaseCartV1 || api;
  if (typeof module !== 'undefined' && module.exports) module.exports = root.SimpPurchaseCartV1;
})(typeof globalThis !== 'undefined' ? globalThis : window);
